import BootstrapModal from 'react-bootstrap/Modal';

import styles from 'css/src/components/common/ui/Drawer.module.scss';

import React from 'react';
import classNames from 'classnames';

import Button, {
    BUTTON_COLORS,
    BUTTON_SHAPES,
    BUTTON_WIDTHS,
} from 'js/src/components/common/ui/Button';
import { prefersReducedMotion } from 'js/src/libs/css-helpers';

type Props = {
    title?: string;
    headerActions?: React.ReactNode;
    show: boolean;
    children: React.ReactNode;
    onShow?: VoidFunction;
    onEntered?: VoidFunction;
    onHide?: VoidFunction;
    drawerClassName?: string;
    backdropClassName?: string;
    titleClassName?: string;
    headerClassName?: string;
    bodyClassName?: string;
    closeButton?: boolean;
};

/**
 * Controlled component of a dismissable sidebar.
 * Custom alternative to <Offcanvas/> in react-bootstrap v5. However, we can't upgrade yet since most of
 * our CSS are still on v4, so we'll implement this based on v4's <Modal/>. The animation, show/hide,
 * and backdrop mechanisms are extended from <Modal/>, but all the contents are highly customized. We
 * even use `dialogAs={customComponent}`, so most of the drawer content's styles are safeguarded from
 * bootstrap.
 */
const Drawer = React.memo(function Drawer({
    title = '',
    headerActions = null,
    show = false,
    onShow,
    onEntered,
    onHide,
    drawerClassName,
    backdropClassName,
    titleClassName = 'font-l5--h5',
    headerClassName,
    bodyClassName,
    closeButton = true,
    children,
}: Props) {
    // callbacks
    const handleOpen = React.useCallback(() => {
        typeof onShow === 'function' && onShow();

        if (prefersReducedMotion()) {
            onEntered && onEntered();
        }
    }, [onEntered, onShow]);

    const handleClose = React.useCallback(() => {
        typeof onHide === 'function' && onHide();
    }, [onHide]);

    const DialogBody = React.useCallback(
        () => (
            <div className={styles['drawer__dialog']}>
                <div className={styles['drawer__content']}>
                    <div className={classNames(styles['drawer__header'], headerClassName)}>
                        <div
                            className={classNames(
                                titleClassName,
                                'drawer__header-title align-self-center font-l5',
                            )}
                        >
                            {title}
                        </div>
                        <div className="drawer__header-actions">
                            {headerActions}
                            {closeButton && (
                                <Button
                                    className="drawer__closer-button"
                                    onClick={handleClose}
                                    iconLeft="close"
                                    // props to make button circular
                                    color={BUTTON_COLORS.SIMPLE}
                                    width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                                    shape={BUTTON_SHAPES.CIRCLE}
                                />
                            )}
                        </div>
                    </div>
                    <div className={classNames(styles['drawer__body'], bodyClassName)}>
                        {children}
                    </div>
                </div>
            </div>
        ),
        [
            bodyClassName,
            children,
            closeButton,
            handleClose,
            headerActions,
            headerClassName,
            title,
            titleClassName,
        ],
    );

    return (
        <BootstrapModal
            animation={!prefersReducedMotion()}
            className={classNames(drawerClassName, styles['drawer'])}
            dialogAs={DialogBody}
            show={show}
            onEntered={onEntered}
            onShow={handleOpen}
            onHide={handleClose}
            backdropClassName={classNames(backdropClassName, styles['drawer__backdrop'])}
            scrollable
        />
    );
});

export default Drawer;
